class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

/**
 * Updates the disabled state of carousel navigation buttons
 * @param {HTMLElement & { current: number; slides: HTMLElement[] | null; infinite: boolean; nextIndex: number; visibleSlides: HTMLElement[] }} slideshow
 * @param {HTMLElement} controls
 */
const updateCarouselButtonStates = (slideshow, controls) => {
  if (!slideshow || !controls) return;

  const prevButton = controls.querySelector('[data-carousel-button="previous"]');
  const nextButton = controls.querySelector('[data-carousel-button="next"]');

  if (!prevButton || !nextButton) return;

  const slides = slideshow.slides;
  const infinite = slideshow.infinite;
  const visibleSlides = slideshow.visibleSlides;

  if (!slides || !slides.length || !visibleSlides || !visibleSlides.length) return;

  const firstVisibleSlide = visibleSlides[0];
  const lastVisibleSlide = visibleSlides[visibleSlides.length - 1];
  
  if (!firstVisibleSlide || !lastVisibleSlide) return;

  const firstVisibleIndex = slides.indexOf(firstVisibleSlide);
  const lastVisibleIndex = slides.indexOf(lastVisibleSlide);

  const isAtStart = firstVisibleIndex === 0;
  const isAtEnd = lastVisibleIndex === slides.length - 1;

  if (prevButton instanceof HTMLButtonElement) {
    const isDisabled = !infinite && isAtStart;
    prevButton.disabled = isDisabled;
    prevButton.setAttribute('aria-disabled', String(isDisabled));
  }

  if (nextButton instanceof HTMLButtonElement) {
    const isDisabled = !infinite && isAtEnd;
    nextButton.disabled = isDisabled;
    nextButton.setAttribute('aria-disabled', String(isDisabled));
  }
};

const initProductRecommendationsCarouselControls = () => {
  // @ts-ignore
  if (window.__productRecommendationsCarouselControlsInitialized) return;
  // @ts-ignore
  window.__productRecommendationsCarouselControlsInitialized = true;

  const SlideshowComponent = customElements.get('slideshow-component');
  if (!SlideshowComponent) return;

  const updateAllButtonStates = () => {
    document.querySelectorAll('product-recommendations').forEach((section) => {
      const controls = section.querySelector('[data-carousel-controls]');
      if (!controls || !(controls instanceof HTMLElement)) return;

      const targetRef = controls.dataset?.carouselTarget;
      if (!targetRef) return;

      const slideshow = section.querySelector(`slideshow-component[ref="${targetRef}"]`);
      if (!slideshow) return;

      // @ts-ignore
      if (!(slideshow instanceof SlideshowComponent)) return;

      updateCarouselButtonStates(/** @type {any} */ (slideshow), controls);
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-carousel-button]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (button.disabled) {
      event.preventDefault();
      return;
    }

    const action = button.dataset.carouselButton;
    if (!action) return;

    const controls = button.closest('[data-carousel-controls]');
    if (!(controls instanceof HTMLElement)) return;

    const targetRef = controls.dataset?.carouselTarget;
    if (!targetRef) return;

    event.preventDefault();

    const section = button.closest('product-recommendations');
    const targetSlideshow =
      section?.querySelector(`slideshow-component[ref="${targetRef}"]`) ||
      document.querySelector(`slideshow-component[ref="${targetRef}"]`);

    if (!targetSlideshow) return;
    // @ts-ignore
    if (!(targetSlideshow instanceof SlideshowComponent)) return;

    // @ts-ignore
    const handler = targetSlideshow[action];
    if (typeof handler !== 'function') return;

    handler.call(targetSlideshow);
  });

  /**
   * @param {any} slideshow
   * @param {HTMLElement} controls
   */
  const handleSlideshowUpdate = (slideshow, controls) => {
    requestAnimationFrame(() => {
      updateCarouselButtonStates(slideshow, controls);
    });
  };

  document.addEventListener('slideshow:select', (event) => {
    const target = event.target;
    if (!target) return;
    // @ts-ignore
    if (!(target instanceof SlideshowComponent)) return;

    const slideshow = /** @type {any} */ (target);
    if (!(slideshow instanceof HTMLElement)) return;

    const section = slideshow.closest('product-recommendations');
    if (!section) return;

    const controls = section.querySelector('[data-carousel-controls]');
    if (!(controls instanceof HTMLElement)) return;

    const targetRef = controls.dataset?.carouselTarget;
    if (!targetRef) return;

    if (slideshow.getAttribute('ref') !== targetRef) return;

    handleSlideshowUpdate(slideshow, controls);
  });

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let scrollTimeout;
  document.addEventListener('scroll', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const slideshow = target.closest('slideshow-component');
    if (!slideshow) return;
    // @ts-ignore
    if (!(slideshow instanceof SlideshowComponent)) return;

    const section = slideshow.closest('product-recommendations');
    if (!section) return;

    const controls = section.querySelector('[data-carousel-controls]');
    if (!(controls instanceof HTMLElement)) return;

    const targetRef = controls.dataset?.carouselTarget;
    if (!targetRef) return;

    if (slideshow.getAttribute('ref') !== targetRef) return;

    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      handleSlideshowUpdate(/** @type {any} */ (slideshow), controls);
    }, 100);
  }, true);

  const observer = new MutationObserver(() => {
    updateAllButtonStates();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAllButtonStates);
  } else {
    updateAllButtonStates();
  }
};

initProductRecommendationsCarouselControls();
